import { GanttConfig } from '../GanttConfig';
import { addSeconds } from 'date-fns';
import {
  GanttQueue,
  NodeQueueEmptyState,
  NodeType,
  QueueEmptyNode,
  QueueMultipleNode,
  QueueNode,
  TimelineNode,
} from '../types';
import { calculateNodeWidth } from './calculateNodeWidth';
import { v4 } from 'uuid';
import { ScaleFilter } from '../components/Timeline/TimelineFilterChip';

// without emptyNodes
export type PartialTimelineNode = QueueNode | QueueMultipleNode;

type GenerateNodesProps = {
  rangeStartDate: Date;
  rangeEndDate: Date;
  nodes: QueueNode[];
  currentFilter: ScaleFilter;
  minNodeWidth: number;
};

export const generateQueues = (
  queue: GanttQueue,
  rangeStartDate: Date,
  rangeEndDate: Date,
  currentFilter: ScaleFilter,
  // 44px default min node width
  minNodeWidth = GanttConfig.SINGLE_ELEMENT_WIDTH / 4,
) => {
  return {
    ...queue,
    queueNodes: generateNodes({ rangeStartDate, rangeEndDate, nodes: queue.queueNodes, currentFilter, minNodeWidth }),
  };
};

const generateNodes = ({
  rangeStartDate,
  rangeEndDate,
  nodes = [],
  currentFilter,
  minNodeWidth,
}: GenerateNodesProps): TimelineNode[] => {
  const now = new Date();

  // Add empty nodes to empty queue
  if (nodes.length === 0) {
    return createEmptyQueue({ now, rangeEndDate, rangeStartDate, currentFilter });
  }

  return nodes
    .toSorted(sortByStartDate)
    .map(adjustDatesToRange({ rangeStartDate, rangeEndDate }))
    .reduce(mergeOverlappingRanges(currentFilter), [] as PartialTimelineNode[])
    .map(calculateNodeWidthMap(currentFilter))
    .reduce(mergeOrStretchSmallNodes(currentFilter, minNodeWidth), [] as PartialTimelineNode[])
    .reduce(addEmptyNodes({ rangeStartDate, rangeEndDate, currentFilter, now }), [] as TimelineNode[]);
};

// Queue without nodes, should contain 2 empty nodes.
const createEmptyQueue = ({
  rangeEndDate,
  rangeStartDate,
  now,
  currentFilter,
}: Pick<GenerateNodesProps, 'rangeStartDate' | 'rangeEndDate' | 'currentFilter'> & { now: Date }) =>
  [
    { id: 'REMOVE', startDate: rangeStartDate, endDate: rangeStartDate } as PartialTimelineNode,
    { id: 'REMOVE', startDate: rangeEndDate, endDate: rangeEndDate } as PartialTimelineNode,
  ]
    .reduce(addEmptyNodes({ rangeStartDate, rangeEndDate, currentFilter, now }), [] as TimelineNode[])
    .filter(({ id }) => id !== 'REMOVE');

const sortByStartDate = (a: QueueNode, b: QueueNode) => a.startDate.getTime() - b.startDate.getTime();

const adjustDatesToRange =
  ({ rangeStartDate, rangeEndDate }: Pick<GenerateNodesProps, 'rangeStartDate' | 'rangeEndDate'>) =>
  (node: QueueNode): QueueNode => {
    return {
      ...node,
      startDate: node.startDate < rangeStartDate ? rangeStartDate : node.startDate,
      endDate: node.endDate > rangeEndDate ? rangeEndDate : node.endDate,
    };
  };

const mergeOverlappingRanges =
  (currentFilter: ScaleFilter) =>
  (acc: PartialTimelineNode[], currentNode: QueueNode): PartialTimelineNode[] => {
    const createMultipleNode = createMultipleNodeFactory(currentFilter);
    // If acc is empty, add the first element
    if (acc.length === 0) {
      return [currentNode];
    }

    const lastNode = acc[acc.length - 1] as QueueMultipleNode;
    const lastNodeShallowCopy = { ...lastNode } as unknown as QueueNode;

    if (currentNode.startDate < lastNode.endDate) {
      // Merge the ranges by extending the endDate

      const overlappingNodes = [
        ...[!lastNode.overlappingNodes ? lastNodeShallowCopy : undefined],
        ...(lastNode?.overlappingNodes || []),
        currentNode,
      ].filter((node): node is QueueNode => !!node);

      // override last node (previous node)
      acc[acc.length - 1] = createMultipleNode({
        startDate: lastNode.startDate,
        endDate: currentNode.endDate > lastNode.endDate ? currentNode.endDate : lastNode.endDate,
        overlappingNodes,
      });

      return acc;
    } else {
      // No overlap, add the currentNode as next node
      return [...acc, currentNode];
    }
  };

const calculateNodeWidthMap =
  (currentFilter: ScaleFilter) =>
  (node: PartialTimelineNode): PartialTimelineNode => {
    return {
      ...node,
      nodeWidth: calculateNodeWidth(currentFilter, node),
    };
  };

const mergeOrStretchSmallNodes =
  (currentFilter: ScaleFilter, minNodeWidth: number) =>
  (
    acc: PartialTimelineNode[],
    currentNode: PartialTimelineNode,
    index: number,
    arr: PartialTimelineNode[],
  ): PartialTimelineNode[] => {
    const createMultipleNode = createMultipleNodeFactory(currentFilter);

    // nodeWidth is ok
    if (currentNode.nodeWidth >= minNodeWidth) {
      return [...acc, currentNode];
    }

    // Cast to possibly undefined - wrong typescript configuration
    const nextNode = arr[index + 1] as PartialTimelineNode | undefined;

    // Try to stretch too small node if space available.
    const stretchedEndDate = calculateStretchedEndDate(currentNode.startDate, currentFilter, minNodeWidth);

    if (!nextNode || (nextNode?.startDate && nextNode.startDate >= stretchedEndDate)) {
      return [
        ...acc,
        {
          ...currentNode,
          nodeWidth: minNodeWidth,
          stretchedEndDate,
        },
      ];
    }

    // Merge small node to nextNode
    // create shallow copies
    const currentNodeShallowCopy = { ...currentNode };
    const nextNodeShallowCopy = { ...nextNode };

    const overlappingNodesWithMultiple = [
      ...(currentNode.overlappingNodes || [currentNodeShallowCopy]),
      nextNodeShallowCopy,
    ] as PartialTimelineNode[];

    // reduce possible multiple nodes
    const overlappingNodes = overlappingNodesWithMultiple.reduce((acc, node) => {
      if (node.type === NodeType.MULTIPLE) {
        return [...acc, ...node.overlappingNodes];
      }

      return [...acc, node];
    }, [] as QueueNode[]);

    // Merge the ranges by extending the endDate
    // recalculate width
    // override last node (previous node)
    arr[index + 1] = createMultipleNode({
      overlappingNodes,
      startDate: currentNode.startDate < nextNode.startDate ? currentNode.startDate : nextNode.startDate,
      endDate: nextNode.endDate,
    });

    return acc;
  };

const addEmptyNodes =
  ({
    rangeStartDate,
    rangeEndDate,
    currentFilter,
    now,
  }: Pick<GenerateNodesProps, 'rangeStartDate' | 'rangeEndDate' | 'currentFilter'> & { now: Date }) =>
  (
    acc: TimelineNode[],
    currentNode: PartialTimelineNode,
    index: number,
    array: PartialTimelineNode[],
  ): TimelineNode[] => {
    const createEmptyNode = createEmptyNodeFactory(now, currentFilter);
    const prevNodeEndDate =
      acc.length > 0 ? acc[acc.length - 1]?.stretchedEndDate || acc[acc.length - 1]?.endDate : rangeStartDate;
    const currentStartDate = currentNode.startDate;

    // Fill gap before the current node
    if (prevNodeEndDate && currentStartDate > prevNodeEndDate) {
      // split node to future and past
      if (prevNodeEndDate < now && currentStartDate > now) {
        const pastEmptyNode = createEmptyNode(prevNodeEndDate, now);
        acc.push(pastEmptyNode);

        const futureEmptyNode = createEmptyNode(now, currentStartDate);
        acc.push(futureEmptyNode);
      } else {
        const emptyNode = createEmptyNode(prevNodeEndDate, currentStartDate);
        acc.push(emptyNode);
      }
    }

    // Add the current node
    acc.push(currentNode);

    const currentNodeEndDate = currentNode.stretchedEndDate || currentNode.endDate;

    // If it's the last node, check for a gap after it
    if (index === array.length - 1 && currentNodeEndDate < rangeEndDate) {
      const endEmptyNode = createEmptyNode(currentNodeEndDate, rangeEndDate);
      acc.push(endEmptyNode);
    }

    return acc;
  };

// calculate calculateStretchedEndDate for minimal node width
const calculateStretchedEndDate = (startDate: Date, currentFilter: ScaleFilter, minNodeWidth: number): Date => {
  const scaleFactor = GanttConfig.SINGLE_ELEMENT_WIDTH / currentFilter.measureScale;
  const hoursDifference = minNodeWidth / scaleFactor;
  const secondsDiff = hoursDifference * 60 * 60;

  return addSeconds(startDate, secondsDiff);
};

const createEmptyNodeFactory =
  (now: Date, currentFilter: ScaleFilter) =>
  (startDate: Date, endDate: Date): QueueEmptyNode => {
    return {
      startDate,
      endDate,
      state: startDate < now ? NodeQueueEmptyState.PAST : NodeQueueEmptyState.FUTURE,
      nodeWidth: calculateNodeWidth(currentFilter, { startDate, endDate }),
      type: NodeType.EMPTY,
      id: v4(),
    };
  };

const createMultipleNodeFactory =
  (currentFilter: ScaleFilter) =>
  ({
    startDate,
    endDate,
    overlappingNodes,
  }: Pick<QueueMultipleNode, 'startDate' | 'endDate' | 'overlappingNodes'>): QueueMultipleNode => {
    return {
      id: overlappingNodes
        .filter(({ type }) => type !== NodeType.BREAK)
        .map(({ id }) => id)
        .join('-'),
      endDate,
      startDate,
      nodeWidth: calculateNodeWidth(currentFilter, { startDate, endDate }),
      type: NodeType.MULTIPLE,
      overlappingNodes,
      hasBreak: overlappingNodes.some(({ type }) => type === NodeType.BREAK),
      tasksAmount: overlappingNodes.filter(({ type }) => type !== NodeType.BREAK).length,
    };
  };
